import { PutObjectCommand, DeleteObjectCommand } from '@aws-sdk/client-s3';
import { r2Client, BUCKET_NAMES, ALLOWED_CONTENT_TYPES, R2_PUBLIC_URLS } from '@/lib/cloudflare';

export const useCloudflareStorage = () => {
  const validateR2Config = () => {
    if (!r2Client) {
      throw new Error('Error de configuración: El cliente de Cloudflare R2 no está configurado');
    }
  };

  const uploadFile = async (file: File, type: 'VIDEO' | 'AUDIO' | 'DOCUMENT' | 'IMAGE') => {
    try {
      console.log('Iniciando useCloudflareStorage.uploadFile:', {
        fileName: file.name,
        fileType: file.type,
        fileSize: file.size,
        uploadType: type
      });

      validateR2Config();

      // Validar tipo de archivo
      const contentType = file.type;
      if (!ALLOWED_CONTENT_TYPES[type].types.includes(contentType)) {
        throw new Error(`Tipo de archivo no permitido: ${contentType}. Los tipos permitidos son: ${ALLOWED_CONTENT_TYPES[type].types.join(', ')}`);
      }

      // Validar tamaño del archivo
      if (file.size > ALLOWED_CONTENT_TYPES[type].maxSize) {
        const maxSizeMB = ALLOWED_CONTENT_TYPES[type].maxSize / (1024 * 1024);
        throw new Error(`El archivo es demasiado grande. El tamaño máximo permitido es ${maxSizeMB}MB`);
      }

      // Generar nombre único para el archivo
      const extension = file.name.split('.').pop()?.toLowerCase() || '';
      const sanitizedFileName = file.name.replace(/[^a-zA-Z0-9]/g, '-').toLowerCase();
      const fileName = `${type.toLowerCase()}/${Date.now()}-${sanitizedFileName}`;

      console.log('Nombre de archivo generado:', {
        originalName: file.name,
        sanitizedFileName,
        finalFileName: fileName
      });

      // Determinar el bucket según el tipo
      const bucketName = type === 'DOCUMENT' ? BUCKET_NAMES.DOCUMENTS : BUCKET_NAMES.MEDIA;

      // Subir archivo a R2
      const command = new PutObjectCommand({
        Bucket: bucketName,
        Key: fileName,
        Body: file,
        ContentType: contentType,
      });

      await r2Client.send(command);

      // Construir la URL pública
      const baseUrl = type === 'DOCUMENT' ? R2_PUBLIC_URLS.DOCUMENTS : R2_PUBLIC_URLS.MEDIA;
      const publicUrl = `${baseUrl}/${fileName}`;

      console.log('Archivo subido exitosamente:', {
        fileName,
        publicUrl,
        bucket: bucketName
      });

      return publicUrl;
    } catch (error) {
      console.error('Error detallado en useCloudflareStorage:', {
        error,
        errorName: error?.name,
        errorMessage: error?.message,
        errorStack: error?.stack,
        fileName: file.name,
        fileType: file.type,
        fileSize: file.size,
        uploadType: type
      });
      
      // Mejorar el mensaje de error para el usuario
      if (error instanceof Error) {
        if (error.message.includes('credentials')) {
          throw new Error('Error de configuración: Verifica las credenciales de Cloudflare R2');
        } else if (error.message.includes('network')) {
          throw new Error('Error de red: Verifica tu conexión a internet');
        } else if (error.message.includes('NoSuchBucket')) {
          throw new Error('Error de configuración: El bucket especificado no existe');
        }
        throw error;
      }
      throw new Error('Error desconocido al subir el archivo');
    }
  };

  const deleteFile = async (fileUrl: string) => {
    try {
      validateR2Config();

      // Extraer el nombre del archivo de la URL
      const fileName = fileUrl.replace(R2_PUBLIC_URLS.MEDIA + '/', '').replace(R2_PUBLIC_URLS.DOCUMENTS + '/', '');

      // Determinar el bucket basado en el tipo de archivo
      const bucketName = fileName.startsWith('document/') ? BUCKET_NAMES.DOCUMENTS : BUCKET_NAMES.MEDIA;

      const command = new DeleteObjectCommand({
        Bucket: bucketName,
        Key: fileName,
      });

      await r2Client.send(command);
      return true;
    } catch (error) {
      console.error('Error al eliminar archivo:', error);
      throw error;
    }
  };

  return {
    uploadFile,
    deleteFile,
  };
};
